import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Sparkles, Shield, Award, CheckCircle, ArrowRight,
         Star, Users, Palette, Truck, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Layout from '../components/layout/Layout';
import { BRAND, REVIEWS } from '../data/mock';

const AboutPage = () => {
  const values = [
    {
      icon: Sparkles,
      title: "Passion for Art",
      description: "We believe every photo tells a story, and we're passionate about transforming those stories into beautiful anime-style artwork.",
      color: "lavender"
    },
    {
      icon: Shield,
      title: "Quality Promise",
      description: "We use only premium materials and the latest printing technology to ensure your custom print looks amazing and lasts long.",
      color: "mint"
    },
    {
      icon: Heart,
      title: "Customer First",
      description: "Your satisfaction is our priority. From quick responses to free revisions, we go the extra mile for every customer.",
      color: "coral"
    },
    {
      icon: Award,
      title: "Unique Creations",
      description: "No two prints are the same. Each piece is uniquely crafted using AI, making your merchandise truly one-of-a-kind.",
      color: "peach"
    }
  ];

  const stats = [
    { number: "100+",      label: "Happy Customers", icon: Users  },
    { number: "500+",      label: "Designs Created",  icon: Palette },
    { number: "4.9",       label: "Average Rating",   icon: Star   },
    { number: "Pan-India", label: "Delivery",         icon: Truck  }
  ];

  const whyDifferent = [
    "AI-powered artwork that's truly unique",
    "Free preview and up to 2 revisions",
    "Premium 180 GSM cotton fabric",
    "Made-to-order with no inventory waste",
    "Personal attention to every order",
    "Quick turnaround time"
  ];

  const getColorClasses = (color) => {
    const colors = {
      mint:    { bg: 'bg-[hsl(var(--mint))]',        text: 'text-[hsl(var(--mint-dark))]'    },
      lavender:{ bg: 'bg-[hsl(var(--lavender))]',    text: 'text-[hsl(var(--lavender-dark))]'},
      coral:   { bg: 'bg-[hsl(var(--coral-light))]', text: 'text-[hsl(var(--coral))]'        },
      peach:   { bg: 'bg-[hsl(var(--peach))]',       text: 'text-[hsl(var(--peach-dark))]'   }
    };
    return colors[color] || colors.mint;
  };

  return (
    <Layout>

      {/* ── Hero ── */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-[hsl(var(--cream))] via-white to-[hsl(var(--mint))]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block px-4 py-2 bg-[hsl(350,70%,65%)]/10 rounded-full text-sm font-medium text-[hsl(350,70%,55%)] mb-4">
              Our Story
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              About{' '}
              <span className="gradient-text">{BRAND.name}</span>
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              We're a small team with a big passion for art and technology. Our mission is to
              help you transform your cherished memories into wearable art that you'll love.
            </p>
          </div>
        </div>
      </section>

      {/* ── Brand Story ── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">

            {/* Left — Text */}
            <div>
              <span className="inline-block px-4 py-2 bg-[hsl(var(--lavender))]/50 rounded-full text-sm font-medium text-[hsl(var(--lavender-dark))] mb-4">
                The Beginning
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Born from a Love for
                <span className="block mt-1 gradient-text">Art & Technology</span>
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  AuraPrintiqo started with a simple idea: what if we could transform the photos
                  we love into stunning anime-style artwork and wear them? As an anime enthusiast
                  and tech lover, I saw the amazing potential of AI art generation.
                </p>
                <p>
                  After countless experiments and learning sessions, AuraPrintiqo was born. We
                  combine cutting-edge AI technology with premium print-on-demand services to
                  create something truly special — custom artwork that's uniquely yours.
                </p>
                <p>
                  Every order we fulfill is a story, a memory, a moment captured in art. And
                  that's what drives us to deliver the best quality and experience to every
                  customer.
                </p>
              </div>
            </div>

            {/* Right — Image */}
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-soft-lg">
                <img
                  src="/images/contact-cover.png"
                  alt="AI artwork transformation"
                  className="w-full h-[480px] object-cover object-center"
                />
                {/* Subtle gradient overlay at bottom */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </div>

              {/* Floating badge — top right */}
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-4 shadow-soft-lg animate-float">
                <div className="flex items-center gap-2">
                  <Heart className="w-6 h-6 text-[hsl(350,70%,60%)] fill-current" />
                  <span className="font-semibold text-gray-900">Made with Love</span>
                </div>
              </div>

              {/* Floating badge — bottom left */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-4 shadow-soft-lg animate-float" style={{ animationDelay: '1s' }}>
                <div className="flex items-center gap-2">
                  <Award className="w-6 h-6 text-amber-500" />
                  <span className="font-semibold text-gray-900">Premium Quality</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="py-12 bg-gradient-to-r from-[hsl(350,70%,65%)] to-[hsl(20,90%,70%)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <stat.icon className="w-8 h-8 text-white/80 mx-auto mb-2" />
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">{stat.number}</div>
                <div className="text-white/80 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Values ── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-[hsl(var(--mint))]/50 rounded-full text-sm font-medium text-[hsl(var(--mint-dark))] mb-4">
              What We Stand For
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              These principles guide everything we do at AuraPrintiqo
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const colorClasses = getColorClasses(value.color);
              return (
                <Card key={index} className="border-0 shadow-soft hover:shadow-soft-lg transition-all duration-300 hover-lift">
                  <CardContent className="p-6 text-center">
                    <div className={`w-14 h-14 mx-auto rounded-2xl ${colorClasses.bg} flex items-center justify-center mb-4`}>
                      <value.icon className={`w-7 h-7 ${colorClasses.text}`} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{value.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Why Different ── */}
      <section className="py-16 bg-gradient-to-b from-[hsl(var(--cream))] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">

            {/* Left — Image */}
            <div className="relative order-2 lg:order-1">
              <div className="relative rounded-3xl overflow-hidden shadow-soft-lg">
                <img
                  src="/images/Pet Anime collection after 2.webp"
                  alt="Premium custom printing"
                  className="w-full h-[480px] object-cover object-center"
                />
                {/* Dark overlay at bottom */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />

                {/* Label on image */}
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 flex items-center gap-4 shadow-lg">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[hsl(350,70%,65%)] to-[hsl(20,90%,70%)] flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">Premium DTG Printing</p>
                      <p className="text-sm text-gray-500">Vibrant colours that last wash after wash</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating badge */}
              <div className="absolute -top-4 -left-4 bg-white rounded-2xl p-4 shadow-soft-lg animate-float">
                <div className="flex items-center gap-2">
                  <Shield className="w-6 h-6 text-emerald-500" />
                  <span className="font-semibold text-gray-900">Quality Assured</span>
                </div>
              </div>
            </div>

            {/* Right — Text */}
            <div className="order-1 lg:order-2">
              <span className="inline-block px-4 py-2 bg-[hsl(var(--coral-light))]/50 rounded-full text-sm font-medium text-[hsl(var(--coral))] mb-4">
                Why Choose Us
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                What Makes Us Different?
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                We're not just another print-on-demand store. We combine AI technology,
                artistic passion, and customer care to deliver something truly special.
              </p>
              <div className="space-y-4">
                {whyDifferent.map((point, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[hsl(350,70%,65%)] to-[hsl(20,90%,70%)] flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">{point}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-amber-100 rounded-full text-sm font-medium text-amber-700 mb-4">
              Customer Love
            </span>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What Our Customers Say</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {REVIEWS.slice(0, 2).map((review) => (
              <Card key={review.id} className="border-0 shadow-soft">
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-4">"{review.text}"</p>
                  <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[hsl(350,70%,75%)] to-[hsl(20,90%,80%)] flex items-center justify-center text-sm font-semibold text-white">
                      {review.avatar}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{review.name}</p>
                      <p className="text-sm text-gray-500">{review.product}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Create Something Special?
          </h2>
          <p className="text-gray-300 text-lg mb-10 max-w-2xl mx-auto">
            Let's turn your favorite photo into wearable art. We can't wait to work with you!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/collections">
              <Button size="lg" className="w-full sm:w-auto bg-gradient-to-r from-[hsl(350,70%,65%)] to-[hsl(20,90%,70%)] hover:from-[hsl(350,70%,60%)] hover:to-[hsl(20,90%,65%)] text-white px-8 shadow-lg">
                Explore Collections
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href={BRAND.instagram} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white/10 px-8">
                <Instagram className="w-5 h-5 mr-2" />
                Follow Us
              </Button>
            </a>
          </div>
        </div>
      </section>

    </Layout>
  );
};

export default AboutPage;
